import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import AiInsightsCard from "@/components/AiInsightsCard";
import HeroStatusChip from "@/components/HeroStatusChip";
import { getBackendUrl } from "@/lib/backend";
import { toTitleCase } from "@/utils/toTitleCase";
import "./SchoolDetail.css";

const API = `${getBackendUrl()}/api`;
const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);

const TABS = [
  { id: "overview", label: "Health Snapshot", icon: "chart" },
  { id: "identity", label: "Identity Compliance", icon: "shield" },
  { id: "infrastructure", label: "Infrastructure", icon: "building" },
  { id: "data", label: "Data Quality", icon: "clipboard" },
  { id: "actions", label: "Action Plan", icon: "action" },
];

const TabIcon = ({ type }) => {
  if (type === "chart") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 3v18h18" />
        <path d="M7 16v-5" />
        <path d="M12 16V7" />
        <path d="M17 16v-3" />
      </svg>
    );
  }
  if (type === "shield") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      </svg>
    );
  }
  if (type === "building") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 21h18" />
        <path d="M5 21V7l8-4v18" />
        <path d="M19 21V11l-6-4" />
      </svg>
    );
  }
  if (type === "clipboard") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5 4h14v16H5z" />
        <path d="M9 2h6v4H9z" />
        <path d="M9 11l2 2 4-4" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 15.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5Z" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87" />
    </svg>
  );
};

const healthTone = (shi) => {
  if (shi >= 85) return { label: "Excellent", className: "good", color: "#10b981" };
  if (shi >= 70) return { label: "Good", className: "amber", color: "#f59e0b" };
  if (shi >= 50) return { label: "Needs Improvement", className: "amber", color: "#f59e0b" };
  return { label: "Critical", className: "", color: "#ef4444" };
};

const countRiskDomains = (school) => {
  let count = 0;
  if ((school.apaar_percentage ?? 0) < 50) count += 1;
  if (!school.water_available || !school.toilets_available) count += 1;
  if ((school.students_per_classroom ?? 0) > 40) count += 1;
  if (school.data_entry_status !== "completed" || !school.certified) count += 1;
  return count;
};

const downloadSchoolProfile = (school) => {
  const blob = new Blob([JSON.stringify(school, null, 2)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `school_${school.udise_code}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const SchoolDetail = () => {
  const { udiseCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [school, setSchool] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insights, setInsights] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/schools/${udiseCode}`);
      setSchool(response.data);
    } catch (error) {
      console.error("Error fetching school data:", error);
      setSchool(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    setInsights("");
    setActiveTab("overview");
  }, [udiseCode]);

  const generateInsights = async () => {
    setInsightsLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/insights/executive-summary`, {
        params: { udise_code: udiseCode },
      });
      setInsights(res.data?.ai_summary || "");
      toast.success("Insights generated");
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || error?.message || "Failed to generate insights";
      if (status === 401) navigate("/login");
      toast.error(detail);
    } finally {
      setInsightsLoading(false);
    }
  };

  const refreshAll = async () => {
    await fetchData();
    if (insights) await generateInsights();
    toast.success("School data refreshed");
  };

  const derived = useMemo(() => {
    if (!school) return null;
    const shi = school.shi_score ?? 0;
    const tone = healthTone(shi);
    const riskDomains = countRiskDomains(school);
    const missingInfra = [!school.water_available, !school.toilets_available].filter(Boolean).length;
    const apaarGap = Math.max(0, Math.round((85 - (school.apaar_percentage ?? 0)) * 10) / 10);
    const ptrHigh = (school.ptr ?? 0) > 30;
    const capacityCritical = (school.students_per_classroom ?? 0) > 40;
    const needsReview =
      missingInfra > 0 ||
      (school.apaar_percentage ?? 0) < 50 ||
      capacityCritical ||
      school.data_entry_status !== "completed" ||
      !school.certified;

    const healthTitle =
      shi < 50
        ? "Critical school health status"
        : shi < 70
          ? "School health needs improvement"
          : shi < 85
            ? "Moderate school health status"
            : "Strong school health status";

    const healthNarrative =
      shi < 50
        ? "This school needs immediate follow-up because identity completion, infrastructure availability, data certification and capacity indicators are showing high-risk signals."
        : shi < 70
          ? "Several domains need block-level attention — identity gaps, infrastructure verification, or data certification may be limiting overall school health."
          : shi < 85
            ? "School health is stable with selective follow-up recommended on weaker identity or infrastructure indicators."
            : "School health indicators are strong across identity, infrastructure, staffing and data quality domains.";

    const primaryAction =
      missingInfra > 0 && (school.apaar_percentage ?? 0) < 50
        ? "Verify infrastructure records, complete APAAR capture, and certify the pending school profile before the next block review."
        : missingInfra > 0
          ? "Confirm drinking water and toilet availability with block officer evidence before the next review cycle."
          : (school.apaar_percentage ?? 0) < 50
            ? "Prioritize APAAR identity capture for pending students and close the completion gap this week."
            : school.data_entry_status !== "completed" || !school.certified
              ? "Complete data entry and certify the school profile after verification."
              : "Maintain current compliance levels and schedule a routine block review.";

    return {
      shi,
      tone,
      riskDomains,
      missingInfra,
      apaarGap,
      ptrHigh,
      capacityCritical,
      needsReview,
      healthTitle,
      healthNarrative,
      primaryAction,
    };
  }, [school]);

  const scrollToSection = (tabId) => {
    setActiveTab(tabId);
    const el = document.getElementById(`slv-${tabId}`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return (
      <div className="school-level-page">
        <div className="slv-loading-wrap">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  if (!school || !derived) {
    return (
      <div className="school-level-page">
        <div className="slv-not-found">
          <p>School not found</p>
          <button type="button" className="slv-primary-action" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const { tone, riskDomains, missingInfra, apaarGap, ptrHigh, capacityCritical, needsReview } = derived;
  const statusPillClass =
    school.rag_status === "red" ? "critical" : school.rag_status === "green" ? "good" : "amber";

  return (
    <div className="school-level-page" data-testid="school-detail">
      <section className="slv-school-header">
        <div>
          <button
            type="button"
            className="slv-back-link"
            onClick={() => (school.block_code ? navigate(`/block/${school.block_code}`) : navigate(-1))}
          >
            <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to schools
          </button>
          <h1>{school.school_name}</h1>
          <div className="slv-school-meta">
            <span className="slv-meta-pill">
              <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {school.district_name} · {school.block_name}
            </span>
            <span className="slv-meta-pill">
              <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 21h18" />
                <path d="M5 21V7l8-4v18" />
                <path d="M19 21V11l-6-4" />
              </svg>
              UDISE: {school.udise_code}
            </span>
            <span className={`slv-status-pill ${statusPillClass}`}>
              <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
              </svg>
              {tone.label}
            </span>
          </div>
        </div>
        <div className="slv-header-actions">
          <button
            type="button"
            className="slv-icon-button"
            title="Export"
            onClick={() => downloadSchoolProfile(school)}
          >
            <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>
          </button>
          <button type="button" className="slv-primary-action" onClick={refreshAll}>
            <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <path d="M21 3v6h-6" />
            </svg>
            Refresh
          </button>
        </div>
      </section>

      <div className="slv-tabs-wrap" aria-label="School sections">
        <nav className="slv-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`slv-tab${activeTab === tab.id ? " active" : ""}`}
              onClick={() => scrollToSection(tab.id)}
            >
              <TabIcon type={tab.icon} />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="slv-content">
        <section
          id="slv-overview"
          className={`slv-hero-card${tone.className ? ` ${tone.className}` : ""}`}
        >
          <div className="slv-hero-layout">
            <div>
              <div className="slv-health-main">
                <div
                  className={`slv-gauge${tone.className ? ` ${tone.className}` : ""}`}
                  aria-label={`Overall School Health ${derived.shi} out of 100`}
                  style={{
                    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(${tone.color} 0 ${derived.shi}%, rgba(239,68,68,.12) ${derived.shi}% 100%)`,
                  }}
                >
                  <div>
                    <strong style={{ color: tone.color }}>{derived.shi}</strong>
                    <span>/100 SHI</span>
                  </div>
                </div>
                <div className="slv-health-copy">
                  <h2>{toTitleCase(derived.healthTitle)}</h2>
                  <p>{derived.healthNarrative}</p>
                  <div className="slv-status-row">
                    {derived.shi < 70 ? (
                      <HeroStatusChip
                        chipClass="slv-status-chip"
                        onClick={() => document.getElementById("slv-actions")?.scrollIntoView({ behavior: "smooth" })}
                        ariaLabel="Immediate action required — view school action plan"
                      >
                        Immediate Action Required
                      </HeroStatusChip>
                    ) : null}
                    <HeroStatusChip
                      chipClass="slv-status-chip"
                      variant="blue"
                      onClick={riskDomains > 0 ? () => document.getElementById("slv-identity")?.scrollIntoView({ behavior: "smooth" }) : undefined}
                      ariaLabel={
                        riskDomains > 0
                          ? `${riskDomains} risk domains — view identity and compliance details`
                          : undefined
                      }
                    >
                      {riskDomains} Risk Domain{riskDomains !== 1 ? "s" : ""}
                    </HeroStatusChip>
                    <HeroStatusChip chipClass="slv-status-chip" variant="blue" showChevron={false}>
                      School-level View
                    </HeroStatusChip>
                    {derived.shi >= 85 ? (
                      <HeroStatusChip chipClass="slv-status-chip" variant="green" showChevron={false}>
                        Healthy Profile
                      </HeroStatusChip>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className={`slv-summary-note${derived.shi >= 70 ? " good" : ""}`}>
                <div className={`slv-icon-box${derived.shi >= 70 ? " green" : " red"}`}>
                  <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                    <path d="M12 9v4" />
                    <path d="M12 17h.01" />
                  </svg>
                </div>
                <div>
                  <b>Primary school action</b>
                  <span>{derived.primaryAction}</span>
                </div>
              </div>
            </div>

            <div className="slv-metric-grid" aria-label="School KPIs">
              <article className="slv-metric-card success">
                <small>Total Students</small>
                <strong>{fmt(school.total_students)}</strong>
                <span>Enrolment base</span>
              </article>
              <article className={`slv-metric-card${school.total_teachers === 0 ? " danger" : " success"}`}>
                <small>Total Teachers</small>
                <strong>{fmt(school.total_teachers)}</strong>
                <span>
                  {school.total_teachers === 0 ? "No teachers assigned" : `PTR: ${school.ptr} : 1`}
                </span>
              </article>
              <article className={`slv-metric-card${(school.apaar_percentage ?? 0) < 50 ? " danger" : " success"}`}>
                <small>APAAR Coverage</small>
                <strong>{school.apaar_percentage}%</strong>
                <span>
                  {(school.apaar_percentage ?? 0) < 50 ? "Very low identity completion" : "Identity on track"}
                </span>
              </article>
              <article className={`slv-metric-card${capacityCritical ? " warning" : " success"}`}>
                <small>Classrooms</small>
                <strong>{fmt(school.classrooms)}</strong>
                <span>
                  {capacityCritical
                    ? `${school.students_per_classroom?.toFixed(1)} students per classroom`
                    : `${school.students_per_classroom?.toFixed(1) ?? 0} students per room`}
                </span>
              </article>
            </div>
          </div>
        </section>

        <section className="slv-ai-card" id="slv-ai">
          <div className="slv-icon-box purple">
            <svg className="slv-large-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2a5 5 0 0 1 5 5c0 1.3-.5 2.5-1.3 3.4A7 7 0 0 1 19 16v3H5v-3a7 7 0 0 1 3.3-5.6A5 5 0 0 1 12 2Z" />
              <path d="M8 19v3M16 19v3" />
            </svg>
          </div>
          <div>
            <h3>AI Insights for This School</h3>
            <p>
              Generate scoped insights covering root-cause signals, recommended remediation, accountable owners, and
              priority action items for this specific school.
            </p>
          </div>
          <button type="button" className="slv-primary-action" onClick={generateInsights} disabled={insightsLoading}>
            <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 3v18" />
              <path d="M3 12h18" />
              <path d="m5 5 14 14" />
              <path d="m19 5-14 14" />
            </svg>
            {insightsLoading ? "Generating…" : "Generate Insights"}
          </button>
        </section>

        {insights ? (
          <div className="slv-insights-result">
            <AiInsightsCard title="AI Insights for this school" content={insights} loading={insightsLoading} />
          </div>
        ) : null}

        <div className="slv-section-title" id="slv-identity">
          <div>
            <h2>Priority Diagnostics</h2>
            <p>
              Grouped cards replace long flat boxes, making it easier to scan what is healthy, what is missing and what
              needs block-level intervention.
            </p>
          </div>
          <button type="button" className="slv-section-action" onClick={() => scrollToSection("actions")}>
            Create action queue →
          </button>
        </div>

        <section className="slv-domain-grid">
          <article className={`slv-domain-card${(school.apaar_percentage ?? 0) < 50 ? " danger" : " green"}`}>
            <div className="slv-domain-head">
              <div className="slv-icon-box red">
                <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
              </div>
              <span className={`slv-status-dot${(school.apaar_percentage ?? 0) < 50 ? " red" : ""}`} />
            </div>
            <h3>Identity Compliance</h3>
            <div className="slv-domain-score">
              <strong>{school.apaar_percentage}</strong>
              <span>APAAR %</span>
            </div>
            <div className="slv-progress-line">
              <i style={{ width: `${Math.min(school.apaar_percentage ?? 0, 100)}%` }} />
            </div>
            <div className="slv-domain-meta">
              <span>Aadhaar {school.aadhaar_percentage}%</span>
              <span>Gap {apaarGap} pts</span>
            </div>
          </article>

          <article
            id="slv-infrastructure"
            className={`slv-domain-card${missingInfra > 0 ? " danger" : " green"}`}
          >
            <div className="slv-domain-head">
              <div className="slv-icon-box orange">
                <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2s7 8 7 13a7 7 0 0 1-14 0c0-5 7-13 7-13Z" />
                </svg>
              </div>
              <span className={`slv-status-dot${missingInfra > 0 ? " red" : ""}`} />
            </div>
            <h3>Infrastructure</h3>
            <div className="slv-domain-score">
              <strong>{missingInfra}</strong>
              <span>Missing items</span>
            </div>
            <div className="slv-progress-line">
              <i style={{ width: `${Math.max(10, 100 - missingInfra * 50)}%` }} />
            </div>
            <div className="slv-domain-meta">
              <span>{school.water_available ? "Water available" : "Water missing"}</span>
              <span>{school.toilets_available ? "Toilet available" : "Toilet missing"}</span>
            </div>
          </article>

          <article className={`slv-domain-card${ptrHigh || capacityCritical ? " orange" : " green"}`}>
            <div className="slv-domain-head">
              <div className="slv-icon-box green">
                <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M6 21v-2a6 6 0 0 1 12 0v2" />
                </svg>
              </div>
              <span className={`slv-status-dot${ptrHigh || capacityCritical ? " amber" : ""}`} />
            </div>
            <h3>Staffing & Capacity</h3>
            <div className="slv-domain-score">
              <strong>{school.ptr}</strong>
              <span>PTR</span>
            </div>
            <div className="slv-progress-line">
              <i style={{ width: `${Math.min(100, Math.max(10, 100 - (school.ptr ?? 0) * 2))}%` }} />
            </div>
            <div className="slv-domain-meta">
              <span>{fmt(school.total_teachers)} teachers</span>
              <span>{fmt(school.total_students)} students</span>
            </div>
          </article>

          <article
            id="slv-data"
            className={`slv-domain-card${school.data_entry_status !== "completed" || !school.certified ? " danger" : " green"}`}
          >
            <div className="slv-domain-head">
              <div className="slv-icon-box purple">
                <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 4h14v16H5z" />
                  <path d="M9 2h6v4H9z" />
                  <path d="M9 11l2 2 4-4" />
                </svg>
              </div>
              <span
                className={`slv-status-dot${school.data_entry_status !== "completed" || !school.certified ? " red" : ""}`}
              />
            </div>
            <h3>Data Quality</h3>
            <div className="slv-domain-score">
              <strong>{school.data_entry_status === "completed" ? "Complete" : "Pending"}</strong>
              <span>Status</span>
            </div>
            <div className="slv-progress-line">
              <i style={{ width: school.data_entry_status === "completed" && school.certified ? "100%" : "30%" }} />
            </div>
            <div className="slv-domain-meta">
              <span>{school.data_entry_status === "completed" ? "Data entry complete" : "Data entry pending"}</span>
              <span>{school.certified ? "Certified" : "Not certified"}</span>
            </div>
          </article>
        </section>

        <section className="slv-detail-grid" id="slv-students">
          <article className="slv-panel">
            <div className="slv-panel-head">
              <div>
                <h3>School Profile Checklist</h3>
                <p>Operational fields are converted into clear review rows with visible status and next-step interpretation.</p>
              </div>
              <span className={`slv-pill${needsReview ? " red" : " green"}`}>
                {needsReview ? "Needs review" : "On track"}
              </span>
            </div>
            <div className="slv-check-list">
              <div className="slv-check-row">
                <div className="slv-icon-box">
                  <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-8 0v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <div>
                  <h4>Total students</h4>
                  <p>Current enrolment loaded for this school.</p>
                </div>
                <div className="slv-value success">{fmt(school.total_students)}</div>
              </div>
              <div className="slv-check-row">
                <div className="slv-icon-box green">
                  <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M6 21v-2a6 6 0 0 1 12 0v2" />
                  </svg>
                </div>
                <div>
                  <h4>Total teachers</h4>
                  <p>
                    {school.total_teachers === 0
                      ? "No teachers mapped — deployment action required."
                      : ptrHigh
                        ? "PTR is elevated — review staffing allocation."
                        : "Staffing appears adequate from PTR view."}
                  </p>
                </div>
                <div className={`slv-value${school.total_teachers === 0 ? " danger" : " success"}`}>
                  {fmt(school.total_teachers)}
                </div>
              </div>
              <div className="slv-check-row">
                <div className="slv-icon-box orange">
                  <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 21h18" />
                    <path d="M5 21V7l8-4v18" />
                    <path d="M19 21V11l-6-4" />
                  </svg>
                </div>
                <div>
                  <h4>Classroom capacity</h4>
                  <p>
                    {capacityCritical
                      ? `Only ${school.classrooms} classroom${school.classrooms !== 1 ? "s" : ""} shown, creating a severe capacity flag.`
                      : "Classroom capacity is within acceptable range."}
                  </p>
                </div>
                <div className={`slv-value${capacityCritical ? " danger" : " success"}`}>
                  {school.students_per_classroom?.toFixed(1)} / room
                </div>
              </div>
              <div className="slv-check-row">
                <div className="slv-icon-box red">
                  <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2s7 8 7 13a7 7 0 0 1-14 0c0-5 7-13 7-13Z" />
                  </svg>
                </div>
                <div>
                  <h4>Drinking water</h4>
                  <p>
                    {school.water_available
                      ? "Drinking water facility is marked available."
                      : "Availability field is missing or marked unavailable."}
                  </p>
                </div>
                <div className={`slv-value${school.water_available ? " success" : " danger"}`}>
                  {school.water_available ? "Available" : "Not Available"}
                </div>
              </div>
              <div className="slv-check-row">
                <div className="slv-icon-box red">
                  <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 21h18" />
                    <path d="M7 21V8h10v13" />
                    <path d="M9 11h.01M15 11h.01M12 15h.01" />
                  </svg>
                </div>
                <div>
                  <h4>Toilets available</h4>
                  <p>
                    {school.toilets_available
                      ? "Sanitation facility is marked available."
                      : "Sanitation availability needs immediate verification."}
                  </p>
                </div>
                <div className={`slv-value${school.toilets_available ? " success" : " danger"}`}>
                  {school.toilets_available ? "Available" : "Not Available"}
                </div>
              </div>
            </div>
          </article>

          <article className="slv-panel" id="slv-actions">
            <div className="slv-panel-head">
              <div>
                <h3>Recommended Action Queue</h3>
                <p>Focused actions for the block officer, school admin and data-entry reviewer.</p>
              </div>
              <span className={`slv-pill${needsReview ? " red" : " green"}`}>
                {needsReview ? "Critical" : "Stable"}
              </span>
            </div>
            <div className="slv-action-card">
              {missingInfra > 0 && (
                <div className="slv-action-item">
                  <div className="slv-icon-box red">
                    <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                      <path d="M12 9v4" />
                      <path d="M12 17h.01" />
                    </svg>
                  </div>
                  <div>
                    <h4>Verify missing infrastructure</h4>
                    <p>Block officer should confirm drinking water and toilet status and upload evidence.</p>
                  </div>
                </div>
              )}
              {(school.apaar_percentage ?? 0) < 85 && (
                <div className="slv-action-item">
                  <div className="slv-icon-box">
                    <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                      <path d="m9 12 2 2 4-4" />
                    </svg>
                  </div>
                  <div>
                    <h4>Close APAAR completion gap</h4>
                    <p>
                      Prioritize student identity capture because APAAR coverage is only {school.apaar_percentage}%.
                    </p>
                  </div>
                </div>
              )}
              {(school.data_entry_status !== "completed" || !school.certified) && (
                <div className="slv-action-item">
                  <div className="slv-icon-box purple">
                    <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 4h14v16H5z" />
                      <path d="M9 2h6v4H9z" />
                      <path d="M9 11l2 2 4-4" />
                    </svg>
                  </div>
                  <div>
                    <h4>Submit and certify profile</h4>
                    <p>
                      Data entry is pending and certification is unavailable, so the school profile should be locked
                      after verification.
                    </p>
                  </div>
                </div>
              )}
              {school.total_teachers === 0 && (
                <div className="slv-action-item">
                  <div className="slv-icon-box orange">
                    <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M6 21v-2a6 6 0 0 1 12 0v2" />
                    </svg>
                  </div>
                  <div>
                    <h4>Initiate teacher deployment</h4>
                    <p>School has active enrolment but zero assigned teachers — escalate for immediate deployment.</p>
                  </div>
                </div>
              )}
              <div className="slv-action-footer">
                <b>Suggested review cadence</b>
                <span>
                  {needsReview
                    ? "Escalate as a 7-day action item and review status every Monday until infrastructure and identity risks move out of critical."
                    : "Schedule a routine monthly review to maintain compliance across identity, infrastructure and data quality."}
                </span>
              </div>
            </div>
          </article>
        </section>

        <div className="slv-section-title">
          <div>
            <h2>Operational Follow-Up Cards</h2>
            <p>Each card maps a school issue to the person who should act and the expected evidence for closure.</p>
          </div>
          <button type="button" className="slv-section-action" onClick={() => scrollToSection("overview")}>
            Back to top ↑
          </button>
        </div>

        <section className="slv-wide-grid">
          <article className="slv-mini-card">
            <div className="slv-icon-box red">
              <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2s7 8 7 13a7 7 0 0 1-14 0c0-5 7-13 7-13Z" />
              </svg>
            </div>
            <h3>Infrastructure Validation</h3>
            <p>Capture photo evidence and update drinking water and sanitation availability fields.</p>
            <div className="slv-mini-meta">
              <span>Owner</span>
              <b>Block Officer</b>
            </div>
          </article>
          <article className="slv-mini-card">
            <div className="slv-icon-box">
              <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </div>
            <h3>APAAR Catch-Up Drive</h3>
            <p>Use class-wise missing student list and complete identity mapping for pending students.</p>
            <div className="slv-mini-meta">
              <span>Target</span>
              <b>85%+</b>
            </div>
          </article>
          <article className="slv-mini-card">
            <div className="slv-icon-box purple">
              <svg className="slv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 4h14v16H5z" />
                <path d="M9 2h6v4H9z" />
                <path d="M9 11l2 2 4-4" />
              </svg>
            </div>
            <h3>Data Certification</h3>
            <p>Review school record, resolve pending status, and certify the profile after corrections.</p>
            <div className="slv-mini-meta">
              <span>Status</span>
              <b>{school.certified ? "Certified" : "Pending"}</b>
            </div>
          </article>
        </section>
      </div>
    </div>
  );
};

export default SchoolDetail;
